// @ts-expect-error
import type {DataProxy} from 'react-apollo';

import type {
    GetUsersWithQuotas_usersWithQuotas_edges as UsersEdge,
    GetUsersWithQuotas as GetUsersWithQuotasQuery,
    GetUsersWithQuotasVariables,
    UserQuotaFragment,
    GetYearOverviewStatsVariables,
    GetYearOverviewStats as GetYearOverviewStatsQuery,
    GetYearOverviewStats_quotaYearOverview as YearOverviewStats,
    GetYearOverviewStats_quotaYearOverview_months as Month,
    GetUsersWithQuotas_usersWithQuotas_edges_node_quotas as Quota,
    MonthInput,
} from 'nutshell-graphql-types';

import {getCurrentFiscalYear, getYears} from '../../helpers';

import GET_USERS_WITH_QUOTAS from '../queries/get-users-with-quotas.graphql';
import GET_YEAR_OVERVIEW_STATS from '../queries/get-year-overview-stats.graphql';

import {QUERY_VARIABLES} from './use-get-users-with-quotas';

export function addUsersToConnection(cache: DataProxy, users: UserQuotaFragment[]) {
    const existingUsers = cache.readQuery<GetUsersWithQuotasQuery, GetUsersWithQuotasVariables>({
        query: GET_USERS_WITH_QUOTAS,
        variables: QUERY_VARIABLES,
    });

    if (!existingUsers) return;

    const existingUserIds = new Set(
        existingUsers.usersWithQuotas.edges.map((e: UsersEdge) => e.node.id)
    );
    const newUsers = users
        .filter((u) => !existingUserIds.has(u.id))
        .map((u: UserQuotaFragment) => ({__typename: 'UsersEdge' as const, node: u}));

    if (newUsers.length === 0) return;

    const updatedEdges = [...existingUsers.usersWithQuotas.edges, ...newUsers].sort((a, b) =>
        a.node.name.localeCompare(b.node.name)
    );

    cache.writeQuery<GetUsersWithQuotasQuery, GetUsersWithQuotasVariables>({
        query: GET_USERS_WITH_QUOTAS,
        variables: QUERY_VARIABLES,
        data: {
            ...existingUsers,
            usersWithQuotas: {
                ...existingUsers.usersWithQuotas,
                edges: updatedEdges,
            },
        },
    });
}

export function addQuotaToYearOverviewStatsCache(
    cache: DataProxy,
    fiscalYear: number,
    monthValues: {month: number; value: number}[]
) {
    const existingYearOverviewStats = cache.readQuery<
        GetYearOverviewStatsQuery,
        GetYearOverviewStatsVariables
    >({
        query: GET_YEAR_OVERVIEW_STATS,
        variables: {
            fiscalYears: getYears(),
        },
    });

    if (!existingYearOverviewStats) return;

    let total = 0;
    const newQuotaMonthToValues: Record<number, number> = {};
    monthValues.forEach((month) => {
        newQuotaMonthToValues[month.month] = month.value;
        total += month.value;
    });

    const updatedYearOverviewStats = existingYearOverviewStats.quotaYearOverview.map(
        (year: YearOverviewStats) => {
            if (year.fiscalYear !== fiscalYear) return year;

            return {
                ...year,
                yearValue: {
                    __typename: 'Money' as const,
                    amount: year.yearValue.amount + total,
                },
                months: year.months.map((month: Month) => ({
                    ...month,
                    value: {
                        __typename: 'Money' as const,
                        amount: month.value.amount + (newQuotaMonthToValues[month.identifier] ?? 0),
                    },
                })),
            };
        }
    );

    cache.writeQuery<GetYearOverviewStatsQuery, GetYearOverviewStatsVariables>({
        query: GET_YEAR_OVERVIEW_STATS,
        variables: {
            fiscalYears: getYears(),
        },
        data: {
            quotaYearOverview: updatedYearOverviewStats,
        },
    });
}

export function updateYearOverviewStatsCacheFromUpdatingExistingQuota(
    previousUsersWithQuotas: GetUsersWithQuotasQuery | null,
    cache: DataProxy,
    userId: string,
    fiscalYear: number,
    monthValues: MonthInput[]
) {
    if (!previousUsersWithQuotas) return;

    const userWhoseQuotaIsBeingUpdated = previousUsersWithQuotas.usersWithQuotas.edges.find(
        (edge: UsersEdge) => edge.node.id === userId
    );

    if (!userWhoseQuotaIsBeingUpdated) return;

    const quotaBeingUpdated = userWhoseQuotaIsBeingUpdated.node.quotas.find(
        (q: Quota) => q.fiscalYear === fiscalYear
    );
    if (!quotaBeingUpdated) return;

    const prevMonthToQuotaValues: Record<number, number> = {};
    quotaBeingUpdated.months.forEach((month: Month) => {
        prevMonthToQuotaValues[month.identifier] = month.value.amount;
    });

    const existingYearOverviewStats = cache.readQuery<
        GetYearOverviewStatsQuery,
        GetYearOverviewStatsVariables
    >({
        query: GET_YEAR_OVERVIEW_STATS,
        variables: {
            fiscalYears: getYears(),
        },
    });

    if (!existingYearOverviewStats) return;

    let totalDelta = 0;
    const quotaMonthToValuesDelta: Record<number, number> = {};
    monthValues.forEach((month) => {
        quotaMonthToValuesDelta[month.identifier] =
            month.value - (prevMonthToQuotaValues[month.identifier] ?? 0);
        totalDelta += quotaMonthToValuesDelta[month.identifier] ?? 0;
    });

    const updatedYearOverviewStats = existingYearOverviewStats.quotaYearOverview.map(
        (year: YearOverviewStats) => {
            if (year.fiscalYear !== fiscalYear) return year;

            return {
                ...year,
                yearValue: {
                    __typename: 'Money' as const,
                    amount: year.yearValue.amount + totalDelta,
                },
                months: year.months.map((month: Month) => ({
                    ...month,
                    value: {
                        __typename: 'Money' as const,
                        amount:
                            month.value.amount + (quotaMonthToValuesDelta[month.identifier] ?? 0),
                    },
                })),
            };
        }
    );

    cache.writeQuery<GetYearOverviewStatsQuery, GetYearOverviewStatsVariables>({
        query: GET_YEAR_OVERVIEW_STATS,
        variables: {
            fiscalYears: getYears(),
        },
        data: {
            quotaYearOverview: updatedYearOverviewStats,
        },
    });
}

export function removeQuotaFromYearOverviewStatsCache(
    previousUsersWithQuotas: GetUsersWithQuotasQuery | null,
    cache: DataProxy,
    userId: string,
    fiscalYear: number | null | undefined
) {
    if (!previousUsersWithQuotas || !fiscalYear) return;

    const userWhoseQuotaIsBeingDeleted = previousUsersWithQuotas.usersWithQuotas.edges.find(
        (edge: UsersEdge) => edge.node.id === userId
    );

    if (!userWhoseQuotaIsBeingDeleted) return;

    const quotaBeingDeleted = userWhoseQuotaIsBeingDeleted.node.quotas.find(
        (q: Quota) => q.fiscalYear === fiscalYear
    );
    if (!quotaBeingDeleted) return;

    const deletedQuotaMonthToValues: Record<number, number> = {};
    let totalToRemove = 0;
    quotaBeingDeleted.months.forEach((month: Month) => {
        deletedQuotaMonthToValues[month.identifier] = month.value.amount;
        totalToRemove += month.value.amount;
    });

    const existingYearOverviewStats = cache.readQuery<
        GetYearOverviewStatsQuery,
        GetYearOverviewStatsVariables
    >({
        query: GET_YEAR_OVERVIEW_STATS,
        variables: {
            fiscalYears: getYears(),
        },
    });

    if (!existingYearOverviewStats) return;

    const updatedYearOverviewStats = existingYearOverviewStats.quotaYearOverview.map(
        (year: YearOverviewStats) => {
            if (year.fiscalYear !== fiscalYear) return year;

            return {
                ...year,
                yearValue: {
                    __typename: 'Money' as const,
                    amount: year.yearValue.amount - totalToRemove,
                },
                months: year.months.map((month: Month) => ({
                    ...month,
                    value: {
                        __typename: 'Money' as const,
                        amount:
                            month.value.amount - (deletedQuotaMonthToValues[month.identifier] ?? 0),
                    },
                })),
            };
        }
    );

    cache.writeQuery<GetYearOverviewStatsQuery, GetYearOverviewStatsVariables>({
        query: GET_YEAR_OVERVIEW_STATS,
        variables: {
            fiscalYears: getYears(),
        },
        data: {
            quotaYearOverview: updatedYearOverviewStats,
        },
    });
}

export function updateYearOverviewStatsCacheFromDuplicatingQuotas(
    cache: DataProxy,
    userIds: string[],
    duplicateFromUserId: string,
    fiscalYear: number | null | undefined
) {
    const existingUsersWithQuotas = cache.readQuery<
        GetUsersWithQuotasQuery,
        GetUsersWithQuotasVariables
    >({
        query: GET_USERS_WITH_QUOTAS,
        variables: QUERY_VARIABLES,
    });

    if (!existingUsersWithQuotas) return;
    const duplicateFromUserEdge = existingUsersWithQuotas.usersWithQuotas.edges.find(
        (edge: UsersEdge) => edge.node.id === duplicateFromUserId
    );

    if (!duplicateFromUserEdge) return;

    const duplicateFromUserInfo = duplicateFromUserEdge.node;

    if (!duplicateFromUserInfo) return;

    const duplicateFromUserQuotas: Quota[] = duplicateFromUserInfo.quotas;

    const currentFiscalYear = getCurrentFiscalYear();
    const fiscalYearsToCopy: number[] = fiscalYear
        ? [fiscalYear]
        : duplicateFromUserQuotas
              .filter((quota) => quota.fiscalYear >= currentFiscalYear)
              .map((quota) => quota.fiscalYear);

    const quotasBeingCopied: Quota[] = duplicateFromUserQuotas.filter((quota) =>
        fiscalYearsToCopy.includes(quota.fiscalYear)
    );

    const yearToTotalMap: Record<number, number> = {};
    const quotaMonthToValues: Record<number, Record<number, number>> = {};
    quotasBeingCopied.forEach((quota) => {
        quota.months.forEach((month) => {
            if (!quotaMonthToValues[quota.fiscalYear]) {
                quotaMonthToValues[quota.fiscalYear] = {};
            }

            const valueToAddToOverviewStats = month.value.amount * userIds.length;
            quotaMonthToValues[quota.fiscalYear]![month.identifier] = valueToAddToOverviewStats;

            yearToTotalMap[quota.fiscalYear] =
                (yearToTotalMap[quota.fiscalYear] ?? 0) + valueToAddToOverviewStats;
        });
    });

    const existingYearOverviewStats = cache.readQuery<
        GetYearOverviewStatsQuery,
        GetYearOverviewStatsVariables
    >({
        query: GET_YEAR_OVERVIEW_STATS,
        variables: {
            fiscalYears: getYears(),
        },
    });

    if (!existingYearOverviewStats) return;

    const updatedYearOverviewStats = existingYearOverviewStats.quotaYearOverview.map(
        (year: YearOverviewStats) => {
            if (!fiscalYearsToCopy.includes(year.fiscalYear)) return year;

            return {
                ...year,
                yearValue: {
                    __typename: 'Money' as const,
                    amount: year.yearValue.amount + (yearToTotalMap[year.fiscalYear] ?? 0),
                },
                months: year.months.map((month: Month) => ({
                    ...month,
                    value: {
                        __typename: 'Money' as const,
                        amount:
                            month.value.amount +
                            (quotaMonthToValues[year.fiscalYear]?.[month.identifier] ?? 0),
                    },
                })),
            };
        }
    );

    cache.writeQuery<GetYearOverviewStatsQuery, GetYearOverviewStatsVariables>({
        query: GET_YEAR_OVERVIEW_STATS,
        variables: {
            fiscalYears: getYears(),
        },
        data: {
            quotaYearOverview: updatedYearOverviewStats,
        },
    });
}
